import React, { useState, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import { accessService } from '../../../services/central-api/access';
import moment from 'moment';
import YearDropdown from '../../../components/YearDropdown';
import HolidayModal from './HolidayModal';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function Holidays({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'Holidays'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['Holidays']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [yearFilter, setYearFilter] = useState(filters.year ?? moment().year());
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedHoliday, setSelectedHoliday] = useState<Holiday>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [holidays, setHolidays] = useState<Holiday[]>([]);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessHolidays', filters],
        queryFn: () => accessService.getHolidays(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setHolidays(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(workSchedule: Holiday) {
        try {
            if (workSchedule.id) {
                await accessService.deleteHoliday(workSchedule.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedHoliday(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    useEffect(() => {
        navigation.setParams({
            ...filters,
            year: yearFilter,
            page: 1,
        });
        setFilters(old => ({
            ...old,
            year: yearFilter,
            page: 0,
        }));
    }, [yearFilter]);

    return <Container>
        <Header
            title={translate('holidays')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedHoliday(undefined);
                setModalVisible(true);
            }} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredHolidays')}
                zIndex={2}
                filter={<YearDropdown
                    hideLabel={true}
                    value={yearFilter}
                    setValue={setYearFilter}
                    zIndex={2}
                />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: 'ID',
                        selector: row => row.id,
                        grow: 0
                    },
                    {
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 2
                    },
                    {
                        name: translate('date'),
                        selector: row => moment().year(row.year ?? yearFilter).month(row.month).date(row.day).format('DD/MM/YYYY'),
                        wrap: true,
                    },
                    {
                        name: translate('recurrent'),
                        selector: row => row.year ? translate('no') : translate('yes'),
                        wrap: true,
                    },
                    {
                        name: translate('type'),
                        selector: row => row.hourEnd ? `${translate('until')} - ${row.hourEnd}h` : translate('wholeDay'),
                        wrap: true,
                    },
                    {
                        cell: row => (
                            <DropdownButton
                                actions={[
                                    {
                                        content: translate('editInformation'),
                                        onPress: () => {
                                            setSelectedHoliday(row);
                                            setModalVisible(true);
                                        }
                                    },
                                    {
                                        content: translate('delete'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setSelectedHoliday(row);
                                            setDeleteModalVisible(true);
                                        },
                                    }
                                ]}
                            />
                        ),
                        wrap: true,
                        button: true
                    }]}
                    data={holidays}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        <HolidayModal
            onClose={handleFormClose}
            isModalVisible={isModalVisible}
            holidayId={selectedHoliday?.id}
        />

        {selectedHoliday && <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={selectedHoliday.name}
            onSubmit={async () => {
                if (selectedHoliday) {
                    await confirmDelete(selectedHoliday);
                }
            }}
        />}
    </Container>;
}
